import { AsyncPipe } from '@angular/common';
import { ChangeDetectorRef, Component, effect, inject, input, Pipe, PipeTransform } from '@angular/core';
import { I18NextModule, I18NextService, ITranslationService } from 'angular-i18next';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Coordinates } from 'src/types/graphql';
import { LoadingComponent } from '../../../_shared/components/loading/loading.component';
import { WeatherGQL } from '../land.query.generated';

@Pipe({
  name: 'description',
  standalone: true,
})
export class DescriptionPipe implements PipeTransform {
  transform(code: number): string {
    return 'land.' + WeatherCode[code as 200];
  }
}

@Pipe({
  name: 'round',
  standalone: true,
})
export class RoundPipe implements PipeTransform {
  transform(value: number): number {
    return Math.round(value);
  }
}

@Pipe({
  name: 'hour',
  standalone: true,
})
export class HourPipe implements PipeTransform {
  transform(timestamp: string): string {
    return timestamp.substring(11, 16);
  }
}

@Pipe({
  name: 'weekDay',
  standalone: true,
})
export class WeekDayPipe implements PipeTransform {
  transform(i: number): string {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayOfWeek = new Date(new Date().setHours(23, 59, 59, 999)).getDay();
    switch (i) {
      case 0:
        return 'land.weather.today';
      case 1:
        return 'land.weather.tomorrow';
      default:
        return 'land.weather.' + dayNames[dayOfWeek > 4 ? Math.abs(dayOfWeek - 5) : dayOfWeek + 2];
    }
  }
}

@Component({
  standalone: true,
  imports: [LoadingComponent, AsyncPipe, WeekDayPipe, HourPipe, DescriptionPipe, RoundPipe, I18NextModule],
  selector: 'sharry-weather',
  templateUrl: 'weather.component.html',
  styleUrl: 'weather.component.scss',
})
export class WeatherComponent {
  translateService: ITranslationService = inject(I18NextService);
  weatherGQL = inject(WeatherGQL);
  cd = inject(ChangeDetectorRef);
  weather$: Observable<{ currentWeather: WeatherList; list: WeatherList[][] }> | undefined;
  centerCoordinates = input<Partial<Coordinates>>();

  constructor() {
    effect(() => {
      const today = new Date(new Date().setHours(23, 59, 59, 999));

      const center: Coordinates = { ...this.centerCoordinates() } as Coordinates;
      delete (center as any).__typename;
      this.weather$ = this.weatherGQL.fetch({ ...center }).pipe(
        map(r => r.data.weather?.result?.list),
        map((res_list: WeatherList[]) => {
          const list = (res_list ?? []).map((l, i) => {
            if (!environment.production) {
              const devL = { ...l };
              const date = new Date(
                new Date(l.dt_txt).setFullYear(
                  today.getFullYear(),
                  today.getMonth(),
                  today.getDate() + Math.floor(i / 8),
                ),
              );
              devL.dt = date.getTime();
              // "2023-07-05 00:00:00"
              devL.dt_txt = `${date.getFullYear()}-${(date.getMonth() + 1).toLocaleString('it', {
                minimumIntegerDigits: 2,
              })}-${date.getDate().toLocaleString('it', { minimumIntegerDigits: 2 })} ${date
                .getHours()
                .toLocaleString('it', { minimumIntegerDigits: 2 })}:${date
                .getMinutes()
                .toLocaleString('it', { minimumIntegerDigits: 2 })}:${date
                .getSeconds()
                .toLocaleString('it', { minimumIntegerDigits: 2 })}`;
              return devL;
            }
            // OpenWeather missing milliseconds to dt
            const fixL = { ...l };
            const date = new Date(Number(fixL.dt + '000'));
            fixL.dt = Date.UTC(
              date.getFullYear(),
              date.getMonth(),
              date.getDate(),
              date.getHours(),
              date.getMinutes(),
              date.getSeconds(),
              date.getMilliseconds(),
            );
            return fixL;
          });
          const nextDateWeather = list.find(
            l => Math.abs(new Date(l.dt_txt).getTime() - new Date().getTime()) < 5400000,
          );
          return {
            currentWeather: nextDateWeather,
            list: Object.values(
              (res_list || []).reduce((acc: { [date: string]: WeatherList[] }, l) => {
                const dateStr = l.dt_txt.substring(0, 10);
                if (!acc[dateStr]) {
                  acc[dateStr] = [];
                }
                acc[dateStr].push(l);
                return acc;
              }, {}),
            ),
          };
        }),
      );
      this.cd.detectChanges();
    });
  }
}

export const WeatherCode = {
  200: 'weather.thunderstorm-with-light-rain',
  201: 'weather.thunderstorm-with-rain',
  202: 'weather.thunderstorm-with-heavy-rain',
  210: 'weather.light-thunderstorm',
  211: 'weather.thunderstorm',
  212: 'weather.heavy-thunderstorm',
  221: 'weather.ragged-thunderstorm',
  230: 'weather.thunderstorm-with-light-drizzle',
  231: 'weather.thunderstorm-with-drizzle',
  232: 'weather.thunderstorm-with-heavy-drizzle',
  300: 'weather.light-intensity-drizzle',
  301: 'weather.drizzle',
  302: 'weather.heavy-intensity-drizzle',
  310: 'weather.light-intensity-drizzle-rain',
  311: 'weather.drizzle-rain',
  312: 'weather.heavy-intensity-drizzle-rain',
  313: 'weather.shower-rain-and-drizzle',
  314: 'weather.heavy-shower-rain-and-drizzle',
  321: 'weather.shower-drizzle',
  500: 'weather.light-rain',
  501: 'weather.moderate-rain',
  502: 'weather.heavy-intensity-rain',
  503: 'weather.very-heavy-rain',
  504: 'weather.extreme-rain',
  511: 'weather.freezing-rain',
  520: 'weather.light-intensity-shower-rain',
  521: 'weather.shower-rain',
  522: 'weather.heavy-intensity-shower-rain',
  531: 'weather.ragged-shower-rain',
  600: 'weather.light-snow',
  601: 'weather.snow',
  602: 'weather.heavy-snow',
  611: 'weather.sleet',
  612: 'weather.light-shower-sleet',
  613: 'weather.shower-sleet',
  615: 'weather.light-rain-and-snow',
  616: 'weather.rain-and-snow',
  620: 'weather.light-shower-snow',
  621: 'weather.shower-snow',
  622: 'weather.heavy-shower-snow',
  701: 'weather.mist',
  711: 'weather.smoke',
  721: 'weather.haze',
  731: 'weather.sand/dust-whirls',
  741: 'weather.fog',
  751: 'weather.sand',
  761: 'weather.dust',
  762: 'weather.volcanic-ash',
  771: 'weather.squalls',
  781: 'weather.tornado',
  800: 'weather.clear-sky',
  801: 'weather.few-clouds-11-25%',
  802: 'weather.scattered-clouds-25-50%',
  803: 'weather.broken-clouds-51-84%',
  804: 'weather.overcast-clouds-85-100%',
};

export interface WeatherList {
  dt: number;
  main: {
    temp: number;
    feels_like: number;
    temp_min: number;
    temp_max: number;
    pressure: number;
    sea_level: number;
    grnd_level: number;
    humidity: number;
    temp_kf: number;
  };
  weather: {
    id: number;
    main: string;
    description: string;
    icon: string;
  }[];
  clouds: {
    all: number;
  };
  wind: {
    speed: number;
    deg: number;
    gust: number;
  };
  visibility: number;
  pop: number;
  rain: {
    '3h': number;
  };
  sys: {
    pod: string;
  };
  dt_txt: string;
}
